import Highcharts from 'highcharts';
import 'highcharts/highcharts-more';
import 'highcharts/modules/solid-gauge';
import 'highcharts/modules/heatmap';
import 'highcharts/modules/treemap';
import 'highcharts/modules/funnel';
import 'highcharts/modules/wordcloud';
import 'highcharts/modules/variable-pie';
import 'highcharts/modules/tilemap';
import 'highcharts/modules/sunburst';
import 'highcharts/modules/treegraph';
import 'highcharts/modules/sankey';
import 'highcharts/modules/dependency-wheel';
import 'highcharts/modules/networkgraph';
import 'highcharts/modules/organization';
import 'highcharts/modules/arc-diagram';
import 'highcharts/modules/dumbbell';
import 'highcharts/modules/no-data-to-display';
import ChartingBaseController from '../charting_base_controller';

Highcharts.setOptions({
  lang: { noData: 'No data available', loading: 'Loading…' },
  noData: {
    position: { align: 'center', verticalAlign: 'top', y: 30 },
    style: { fontSize: '18px', fontWeight: 'normal', color: '#333333' }
  }
});

export default class ChartingHighchartsBaseController extends ChartingBaseController {
  static targets = ['chart'];

  static values = {
    config: { type: Object, default: {} },
    data: { type: Array, default: [] },
    actions: { type: Array, default: [] },
    loading: { type: Boolean, default: false }
  };

  chartTargetConnected() {
    this.renderChart();

    this.colorSchemeQuery = window.matchMedia('(prefers-color-scheme: dark)');
    this.handleColorSchemeChange = () => this.renderChart();
    this.colorSchemeQuery.addEventListener(
      'change',
      this.handleColorSchemeChange
    );
  }

  disconnect() {
    this.colorSchemeQuery?.removeEventListener(
      'change',
      this.handleColorSchemeChange
    );
  }

  dataValueChanged() {
    if (this.hasChartTarget) this.renderChart();
  }

  configValueChanged() {
    if (this.hasChartTarget) this.renderChart();
  }

  actionsValueChanged() {
    if (this.hasChartTarget) this.renderChart();
  }

  loadingValueChanged() {
    if (this.hasChartTarget) this.renderChart();
  }

  renderChart() {
    const chartIndex = this.chartTarget.getAttribute('data-highcharts-chart');
    const existing = Highcharts.charts[chartIndex];
    const config = this.buildConfig();

    if (existing) this.toggleLoading(existing);

    if (existing && existing.options.chart?.type !== config.chart?.type) {
      existing.destroy();
      this.chart = Highcharts.chart(this.chartTarget, config);
    } else if (existing) {
      this.chart = existing;
      this.chart.update(config, true, true, false);
    } else {
      this.chart = Highcharts.chart(this.chartTarget, config);
    }

    this.toggleLoading(this.chart);
  }

  toggleLoading(chart) {
    if (this.loadingValue) {
      chart.showLoading();
      chart.hideNoData?.();
    } else {
      chart.hideLoading();
    }
  }

  buildConfig() {
    const defaults = this.themedDefaults();
    const config = {
      ...Highcharts.merge(defaults, this.configValue),
      series: this.seriesWithActions()
    };

    config.tooltip = config.tooltip ?? {};
    if (
      !config.tooltip.formatter
      && !config.tooltip.pointFormat
      && !config.tooltip.headerFormat
    ) {
      const controller = this;
      config.tooltip.formatter = function defaultTooltipFormatter() {
        return controller.tooltipFormatter(this);
      };
    }

    if (Array.isArray(config.yAxis)) {
      config.yAxis = config.yAxis.map((axis) => Highcharts.merge(defaults.yAxis, axis));
    }
    if (Array.isArray(config.xAxis)) {
      config.xAxis = config.xAxis.map((axis) => Highcharts.merge(defaults.xAxis, axis));
    }
    if (config.colorAxis) {
      config.colorAxis = this.themedColorAxis(config.colorAxis);
    }

    if (ChartingHighchartsBaseController.hasDataLabels(config.series)) {
      config.plotOptions = Highcharts.merge(config.plotOptions || {}, {
        series: {
          dataLabels: {
            enabled: true,
            formatter() {
              return this.point.dataLabelText ?? this.y;
            }
          }
        }
      });
    }

    return config;
  }

  static hasDataLabels(series) {
    return series.some((s) => s.data?.some((p) => p?.dataLabelText != null));
  }

  themedColorAxis(colorAxis) {
    const themeAxis = (axis) => ({
      ...axis,
      ...(axis.dataClasses && {
        dataClasses: axis.dataClasses.map((dataClass) => ({
          ...dataClass,
          ...(dataClass.color != null && {
            color: this.resolveThemeColor(dataClass.color)
          })
        }))
      }),
      ...(axis.stops && {
        stops: axis.stops.map(([position, color]) => [
          position,
          this.resolveThemeColor(color)
        ])
      })
    });

    return Array.isArray(colorAxis)
      ? colorAxis.map(themeAxis)
      : themeAxis(colorAxis);
  }

  themedDefaults() {
    return {
      colors: this.palette(),
      chart: {
        backgroundColor: 'transparent',
        style: {
          fontFamily: this.theme('typography-font-family')
        }
      },
      title: {
        text: undefined,
        style: {
          color: this.theme('color-base-content'),
          fontSize: this.theme('typography-title-size'),
          fontWeight: this.theme('typography-title-weight')
        }
      },
      xAxis: {
        labels: {
          style: {
            color: this.theme('color-base-content-secondary'),
            fontSize: this.theme('typography-label-size')
          }
        },
        title: { style: { color: this.theme('color-base-content-secondary') } },
        lineColor: this.theme('color-neutral'),
        tickColor: this.theme('color-neutral'),
        gridLineColor: this.theme('color-neutral')
      },
      yAxis: {
        labels: {
          style: {
            color: this.theme('color-base-content-secondary'),
            fontSize: this.theme('typography-label-size')
          }
        },
        title: { style: { color: this.theme('color-base-content-secondary') } },
        lineColor: this.theme('color-neutral'),
        tickColor: this.theme('color-neutral'),
        gridLineColor: this.theme('color-neutral')
      },
      legend: {
        itemStyle: {
          color: this.theme('color-base-content'),
          fontSize: this.theme('typography-label-size')
        },
        itemHoverStyle: { color: this.theme('color-accent') }
      },
      tooltip: {
        backgroundColor: this.theme('color-base-200'),
        borderColor: this.theme('color-neutral'),
        style: {
          color: this.theme('color-base-content')
        },
        useHTML: true
      },
      loading: {
        style: {
          backgroundColor: 'transparent'
        },
        labelStyle: {
          color: this.theme('color-base-content-secondary'),
          fontSize: this.theme('typography-label-size')
        }
      },
      plotOptions: {
        series: {
          animation: false,
          borderColor: this.theme('color-neutral'),
          dataLabels: {
            style: { color: this.theme('color-base-content') }
          }
        }
      },
      credits: { enabled: false }
    };
  }

  // eslint-disable-next-line class-methods-use-this -- subclasses override this
  tooltipFormatter(ctx) {
    let html = `<b>${ctx.point.name || ctx.series.name}</b><br/>`;
    html += `${ctx.point.category ? `${ctx.point.category}: ` : ''}${ctx.y}`;

    if (ctx.point.tooltip) {
      html += `<br/>${ctx.point.tooltip}`;
    }

    return html;
  }

  seriesWithActions() {
    const series = Array.isArray(this.dataValue)
      ? this.dataValue
      : [this.dataValue];
    const pickAction = this.actionsValue.find((a) => a.trigger === 'pick');

    if (!pickAction) return series;

    return series.map((s) => ({
      ...s,
      cursor: 'pointer',
      point: {
        events: {
          click: (event) => this.handlePointClick(event)
        }
      }
    }));
  }

  handlePointClick(event) {
    const { point } = event;
    const actions = this.actionsValue.filter((a) => a.trigger === 'pick');
    if (actions.length === 0) return;

    const value = point.name || point.category || point.x;
    if (value === undefined || value === null) return;

    this.applyActions(actions, { value });
  }
}
